
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

export interface Friend {
  id: string;
  user_id: string;
  friend_id: string;
  status: "pending" | "accepted" | "rejected";
  created_at: string;
  updated_at?: string;
  friend_profile: {
    id: string;
    username: string;
    first_name: string | null;
    last_name: string | null;
    profile_picture: string | null;
  };
}

export const useFriends = () => {
  const [friends, setFriends] = useState<Friend[]>([]);
  const [pendingRequests, setPendingRequests] = useState<Friend[]>([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  const fetchFriends = async () => {
    if (!user?.id) return;

    try {
      setLoading(true);
      
      // Get accepted friendships (bidirectional - both directions)
      const { data: acceptedFriends, error: friendsError } = await supabase
        .from('friends')
        .select(`
          *,
          friend_profile:profiles!friends_friend_id_fkey(
            id,
            username,
            first_name,
            last_name,
            profile_picture
          )
        `)
        .or(`and(user_id.eq.${user.id},status.eq.accepted),and(friend_id.eq.${user.id},status.eq.accepted)`)
        .eq('status', 'accepted');

      if (friendsError) {
        console.error('Error fetching friends:', friendsError);
      } else {
        // Filter to only show each friendship once (avoid duplicates from bidirectional)
        const uniqueFriends = (acceptedFriends || []).filter(friend => {
          // Show friendship where current user is the user_id (the one who initiated or accepted)
          return friend.user_id === user.id;
        }).map(friend => ({
          ...friend,
          status: friend.status as "pending" | "accepted" | "rejected"
        }));
        
        console.log('Fetched friends:', uniqueFriends.length);
        setFriends(uniqueFriends);
      }

      // Get pending friend requests (where current user is the target)
      const { data: pendingFriendRequests, error: pendingError } = await supabase
        .from('friends')
        .select(`
          *,
          friend_profile:profiles!friends_user_id_fkey(
            id,
            username,
            first_name,
            last_name,
            profile_picture
          )
        `)
        .eq('friend_id', user.id)
        .eq('status', 'pending');

      if (pendingError) {
        console.error('Error fetching pending requests:', pendingError);
      } else {
        const typedRequests = (pendingFriendRequests || []).map(request => ({
          ...request,
          status: request.status as "pending" | "accepted" | "rejected"
        }));
        
        console.log('Fetched pending requests:', typedRequests.length);
        setPendingRequests(typedRequests);
      }

    } catch (error) {
      console.error('Error in fetchFriends:', error);
    } finally {
      setLoading(false);
    }
  };

  const acceptFriendRequest = async (friendshipId: string) => {
    if (!user?.id) return false;

    try {
      const { data, error } = await supabase.rpc('accept_friend_request_with_notification', {
        request_id: friendshipId,
        accepter_id: user.id
      });

      if (error) {
        console.error('Error accepting friend request:', error);
        toast.error("Fehler beim Annehmen der Freundschaftsanfrage");
        return false;
      }

      if (data) {
        // Send email notification
        try {
          await supabase.functions.invoke('send-friendship-notification', {
            body: {
              type: 'friend_request_accepted',
              recipientUserId: user.id,
              requesterUserId: user.id
            }
          });
        } catch (emailError) {
          console.error('Error sending acceptance email:', emailError);
          // Don't fail the main operation if email fails
        }

        toast.success("Freundschaftsanfrage angenommen!");
        await fetchFriends(); // Refresh data
        return true;
      } else {
        toast.error("Freundschaftsanfrage konnte nicht angenommen werden");
        return false;
      }
    } catch (error) {
      console.error('Error in acceptFriendRequest:', error);
      toast.error("Fehler beim Annehmen der Freundschaftsanfrage");
      return false;
    }
  };

  const rejectFriendRequest = async (friendshipId: string) => {
    if (!user?.id) return false;

    try {
      // First get the requester's ID from the friendship
      const { data: friendship, error: fetchError } = await supabase
        .from('friends')
        .select('user_id')
        .eq('id', friendshipId)
        .eq('friend_id', user.id)
        .eq('status', 'pending')
        .single();

      if (fetchError || !friendship) {
        console.error('Error fetching friendship:', fetchError);
        toast.error("Fehler beim Ablehnen der Freundschaftsanfrage");
        return false;
      }

      // Use the new function to reject ALL requests from this user
      const { data, error } = await supabase.rpc('reject_all_friend_requests_from_user', {
        target_user_id: user.id,
        requester_user_id: friendship.user_id
      });

      if (error) {
        console.error('Error rejecting friend requests:', error);
        toast.error("Fehler beim Ablehnen der Freundschaftsanfrage");
        return false;
      }

      if (data) {
        toast.success("Freundschaftsanfrage abgelehnt");
        await fetchFriends(); // Refresh data
        return true;
      } else {
        toast.error("Keine Anfragen gefunden zum Ablehnen");
        return false;
      }
    } catch (error) {
      console.error('Error in rejectFriendRequest:', error);
      toast.error("Fehler beim Ablehnen der Freundschaftsanfrage");
      return false;
    }
  };

  const removeFriend = async (friendId: string) => {
    if (!user?.id) return false;

    try {
      const { data, error } = await supabase.rpc('remove_friend', {
        user1_id: user.id,
        user2_id: friendId
      });

      if (error) {
        console.error('Error removing friend:', error);
        toast.error("Fehler beim Beenden der Freundschaft");
        return false;
      }

      if (data) {
        toast.success("Freundschaft beendet");
        await fetchFriends(); // Refresh data
        return true;
      } else {
        toast.error("Freundschaft konnte nicht beendet werden");
        return false;
      }
    } catch (error) {
      console.error('Error in removeFriend:', error);
      toast.error("Fehler beim Beenden der Freundschaft");
      return false;
    }
  };

  useEffect(() => {
    if (!user?.id) return;

    fetchFriends();
    
    // Real-time subscriptions moved to centralized RealtimeContext
    // Friends data will be automatically refetched when changes occur
  }, [user?.id]);

  return {
    friends,
    pendingRequests,
    loading,
    acceptFriendRequest,
    rejectFriendRequest,
    removeFriend,
    refetch: fetchFriends
  };
};
